import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronRight, Package, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';

import { Spinner } from '@terra/ui';

import { api } from '../lib/api';

/** What a dashboard tile counted, so its drawer lists exactly those orders. */
export interface DashboardDrill {
  /** The tile's name, e.g. "New Orders" or "Pending". */
  label: string;
  /** Order (sub-order) statuses, comma-separated. */
  status?: string;
  /** Parcel statuses, comma-separated. */
  shipmentStatus?: string;
  /** YYYY-MM-DD; the month-to-date tiles count from the 1st. */
  from?: string;
  /** How many the tile showed, for the header while the list loads. */
  count: number;
}

interface DrillOrder {
  id: string;
  status: string;
  totalPaise: number;
  createdAt: string;
  orderGroup: { orderNumber: string; customer: { name: string } | null };
  lineItems: Array<{
    id: string;
    productTitle: string;
    qty: number;
    unitPricePaise: number;
    imageUrl: string | null;
  }>;
  shipment: { status: string; awbNumber: string | null } | null;
}

const PREVIEW_SIZE = 20;

const inr = (paise: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(paise / 100);
const words = (s: string) => s.replace(/_/g, ' ').toLowerCase();

/** The query string that opens the Orders page on the same filter. */
function ordersLinkFor(drill: DashboardDrill): string {
  const params = new URLSearchParams({ view: drill.label });
  if (drill.status) params.set('status', drill.status);
  if (drill.shipmentStatus) params.set('shipmentStatus', drill.shipmentStatus);
  if (drill.from) params.set('from', drill.from);
  return `/orders?${params.toString()}`;
}

/**
 * Slide-over opened from a dashboard tile: the orders behind its number, each
 * with the products in it, so a seller sees *which* items are pending, in
 * transit or cancelled without leaving the dashboard. A row opens the order.
 */
export function DashboardOrdersDrawer({
  drill,
  onClose,
}: {
  drill: DashboardDrill | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const open = drill !== null;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['vendor-dashboard-drill', drill?.status, drill?.shipmentStatus, drill?.from],
    queryFn: () =>
      api.orders.vendorOrders({
        pageSize: PREVIEW_SIZE,
        ...(drill?.status ? { status: drill.status } : {}),
        ...(drill?.shipmentStatus ? { shipmentStatus: drill.shipmentStatus } : {}),
        ...(drill?.from ? { fromDate: new Date(drill.from).toISOString() } : {}),
      }) as Promise<{ data: DrillOrder[]; page: { total: number } }>,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!drill) return null;
  const orders = data?.data ?? [];
  const total = data?.page.total ?? drill.count;

  return createPortal(
    <div className="fixed inset-0 z-[900]">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`${drill.label} orders`}
        className="absolute inset-y-0 right-0 flex w-full max-w-lg flex-col bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{drill.label}</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {total} {total === 1 ? 'order' : 'orders'}
              {drill.from ? ' this month' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="grid h-40 place-items-center">
              <Spinner size="lg" />
            </div>
          ) : isError ? (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 p-6 text-center">
              <p className="text-sm font-semibold text-rose-700">
                Couldn&apos;t load these orders.
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-2 text-xs font-bold text-rose-700 underline"
              >
                Try again
              </button>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-10 text-center">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100 text-slate-400">
                <Package className="h-5 w-5" />
              </div>
              <p className="text-sm font-semibold text-slate-600">Nothing here right now</p>
              <p className="text-xs text-slate-400">
                Orders show up here as soon as they reach this stage.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {orders.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`/orders/${o.id}`);
                    }}
                    className="group w-full rounded-2xl border border-slate-100 p-4 text-left transition-all hover:border-indigo-200 hover:shadow-md"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-900">
                          #{o.orderGroup.orderNumber}
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(o.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                          {o.orderGroup.customer?.name ? ` · ${o.orderGroup.customer.name}` : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold capitalize text-slate-600">
                          {words(o.shipment && drill.shipmentStatus ? o.shipment.status : o.status)}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-indigo-500" />
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {o.lineItems.map((li) => (
                        <div key={li.id} className="flex items-center gap-3">
                          <div className="grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-100 bg-slate-50">
                            {li.imageUrl ? (
                              <img
                                src={li.imageUrl}
                                alt=""
                                className="h-full w-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <Package className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-800">
                              {li.productTitle}
                            </p>
                            <p className="text-xs text-slate-500">
                              Qty {li.qty} × {inr(li.unitPricePaise)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 flex items-center justify-between border-t border-slate-50 pt-3 text-xs">
                      <span className="font-mono text-slate-400">
                        {o.shipment?.awbNumber ? `AWB ${o.shipment.awbNumber}` : ''}
                      </span>
                      <span className="font-bold text-slate-800">{inr(o.totalPaise)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
          <span className="text-xs text-slate-400">
            {orders.length > 0 && total > orders.length
              ? `Showing ${orders.length} of ${total}`
              : ''}
          </span>
          <Link
            to={ordersLinkFor(drill)}
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-800"
          >
            Open in Orders <ArrowRight className="h-4 w-4" />
          </Link>
        </footer>
      </aside>
    </div>,
    document.body,
  );
}
